import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { expiryText, fullText, isFull, memberCountText, normalizeInviteCode } from "../circles/circles";
import { chooseCircle } from "../circles/choice";
import { AskControl, SupervisionTerms } from "../components/MySupervision";
import { localTimeZone, scheduleText } from "../events/events";
import { radiusText } from "../sharing/bubbles";
import { acceptErrorText, actionMode, noticeText, supervisionPoll } from "../supervision/member";
import { useUnits } from "../useUnits";
import { useNow } from "../useNow";

/** Refreshes the expiry label this often. */
const TICK_MS = 30_000;
/** The bubble joining a friends circle starts. */
const FRIENDS_BUBBLE_M = 2000;
const NOT_FOUND = "This invite link is invalid, has expired, or has been used up. Ask for a new one.";

/** `/join/<code>`: an invite link opened in the browser. */
export function JoinPage() {
  const code = normalizeInviteCode(useParams().code ?? "");
  // A fresh subtree per code: one invite's pending or failed join never shows on another.
  return <JoinInvite key={code ?? ""} code={code} />;
}

function JoinInvite({ code }: { code: string | null }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const nowMs = useNow(TICK_MS);
  const [viewerZone] = useState(localTimeZone);
  const units = useUnits();
  const preview = useQuery({
    queryKey: ["invite", code],
    queryFn: async () => unwrap(await api.GET("/invites/{code}", { params: { path: { code: code! } } })),
    enabled: code !== null,
  });
  // The caller's own supervisions: joining may need a guardian's approval, or tell them.
  const circles = useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
    refetchInterval: (query) => supervisionPoll(query.state.data),
  });
  const joinMode = actionMode("join_circle", circles.data ?? []);
  const accept = useMutation({
    mutationFn: async ({ inviteCode, hash }: { inviteCode: string; hash?: string | undefined }) =>
      unwrap(
        await api.POST("/invites/{code}/accept", {
          params: { path: { code: inviteCode } },
          ...(hash && { body: { supervision_hash: hash } }),
        }),
      ),
    onSuccess: async (circle) => {
      if (session) chooseCircle(session.userId, circle.id);
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
    // Revoked, expired, used up or full since the preview: show that instead of a stale Join button.
    // A supervised invite whose controls or guardians changed since: show them again.
    onError: async (error, { inviteCode }) => {
      if (
        error instanceof ApiError &&
        (error.status === 404 || error.code === "circle_full" || error.code === "supervision_consent_required")
      ) {
        await queryClient.invalidateQueries({ queryKey: ["invite", inviteCode] });
      }
      // A supervision started or tightened since the page loaded: show "Ask to join".
      if (error instanceof ApiError && error.code === "supervised") {
        await queryClient.invalidateQueries({ queryKey: ["circles"] });
      }
    },
  });

  if (code === null) {
    return (
      <JoinLayout>
        <p className="error">{NOT_FOUND}</p>
      </JoinLayout>
    );
  }
  if (preview.isPending) {
    return (
      <JoinLayout>
        <p className="muted">Checking the invite…</p>
      </JoinLayout>
    );
  }
  if (preview.error) {
    return (
      <JoinLayout>
        <p className="error">{errorMessage(preview.error, { 404: NOT_FOUND })}</p>
      </JoinLayout>
    );
  }

  const invite = preview.data;
  const expired = Date.parse(invite.expires_at) <= nowMs;
  const kind = invite.circle_kind;
  const counts = { member_count: invite.member_count, member_limit: invite.member_limit ?? null };
  // Full now, or the accept said so (someone took the last place since the preview).
  // Join stays enabled when the preview is full: someone already in the circle may still accept (200).
  // After a refusal it is disabled until a newer preview shows room.
  const refused = accept.error instanceof ApiError && accept.error.code === "circle_full" && isFull(counts);
  const full = isFull(counts);
  const supervision = invite.supervision ?? null;
  const hash = supervision ? invite.supervision_hash : undefined;
  const acceptError =
    accept.error instanceof ApiError && accept.error.code === "supervised"
      ? "Joining another circle needs a guardian's approval now. Ask them below."
      : accept.error instanceof ApiError
        ? acceptErrorText(accept.error.code)
        : null;
  return (
    <JoinLayout>
      <div className="card stack">
        <h2>{invite.circle_name}</h2>
        {invite.event && <p className="join-schedule">{scheduleText(invite.event, viewerZone)}</p>}
        <p>
          {invite.invited_by ? `${invite.invited_by} invited you to join.` : "You've been invited to join."}{" "}
          {memberCountText(counts)}.
        </p>
        {kind === "friends" ? (
          <>
            <p>
              <strong>This is a friends circle.</strong> You'll join sharing an area of about{" "}
              {radiusText(FRIENDS_BUBBLE_M, units)} around you instead of your exact location. You can switch to your
              exact location on the circle's page whenever you like.
            </p>
            <p className="muted">
              Your SOS alerts reach this circle only if you turn on "Send my SOS here".{" "}
              {expiryText(invite.expires_at, nowMs)}.
            </p>
          </>
        ) : kind === "event" ? (
          <>
            {invite.event?.description && <p className="event-description">{invite.event.description}</p>}
            <p className="muted">
              Members see your location and battery level only while the event is on, from its start to its end, and
              your history from that time until the event, its chat and places are deleted 30 days after it ends.{" "}
              {expiryText(invite.expires_at, nowMs)}.
            </p>
          </>
        ) : (
          <p className="muted">
            Members of this circle will see your location, battery level and history while you share them.{" "}
            {expiryText(invite.expires_at, nowMs)}.
          </p>
        )}
        {supervision && (
          <div className="stack supervised-invite">
            <h3>This invite makes you a supervised member</h3>
            <p>
              Whoever joins with it is supervised in {invite.circle_name}: some of what you do there needs a guardian's
              approval, or they're told. Read it before you join.
            </p>
            <SupervisionTerms
              controls={supervision.controls}
              guardians={supervision.guardians}
              selfReleaseDays={supervision.self_release_days}
              circleName={invite.circle_name}
            />
          </div>
        )}
        {full ? (
          <p className="error" role="status">
            {fullText(kind)}. Ask {invite.invited_by ?? "whoever invited you"} to make room.
          </p>
        ) : (
          accept.error && <p className="error">{acceptError ?? errorMessage(accept.error, { 404: NOT_FOUND })}</p>
        )}
        {joinMode === "ask" ? (
          <AskControl
            action="join_circle"
            params={{ invite_code: code, ...(hash && { supervision_hash: hash }) }}
            label={`Ask to join ${invite.circle_name}`}
            intro={
              <p className="muted">
                Your supervision asks a guardian before you join another circle. They see its name, kind and size, never
                the invite. You join once a guardian approves in each circle that asks for it.
              </p>
            }
          />
        ) : (
          <>
            {noticeText(joinMode) && (
              <p className="muted">Your guardians are told that you joined {invite.circle_name}.</p>
            )}
            <button
              type="button"
              disabled={accept.isPending || expired || refused}
              onClick={() =>
                accept.mutate({ inviteCode: code, hash }, { onSuccess: () => void navigate("/", { replace: true }) })
              }
            >
              {supervision ? `Accept supervision and join ${invite.circle_name}` : `Join ${invite.circle_name}`}
            </button>
          </>
        )}
      </div>
    </JoinLayout>
  );
}

function JoinLayout({ children }: { children: ReactNode }) {
  return (
    <div className="stack join-page">
      <h1>Join</h1>
      {children}
      <p>
        <Link to="/circles">Your circles</Link>
      </p>
    </div>
  );
}
